import { ChangeEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { filter, flatten, uniq, orderBy } from 'lodash';
import {
  resetFilters,
  selectFilters,
  selectTopics,
  setFilters,
  useAppDispatch,
} from '../../../../store';
import { EventType, IBookingEvent } from '../../../../types';
import { useTopicFilter } from '../../../shared/filters';

interface UseOfferingFiltersProps {
  upcomingEvents: IBookingEvent[];
}

const useOfferingFilters = ({ upcomingEvents }: UseOfferingFiltersProps) => {
  const { topics } = useSelector(selectTopics);
  const { t, i18n } = useTranslation();
  const filters = useSelector(selectFilters);
  const [languageFilter, setLanguageFilter] = useState<number | string>(0);
  const [eventTypeFilter, setEventTypeFilter] = useState<string>('');
  const [languages, setLanguages] = useState<Set<string>>(new Set());
  const [filteredUpcomingEvents, setFilteredUpcomingEvents] = useState<
    IBookingEvent[]
  >([]);
  const dispatch = useAppDispatch();

  const filterByEventType = (events: Array<IBookingEvent>) => {
    if (eventTypeFilter === '') {
      return events;
    }

    return events.filter(
      (event: IBookingEvent) => event.type === eventTypeFilter,
    );
  };

  const getFilterOptions = (languages: any) => {
    const allLanguages = {
      id: 'language',
      value: 0,
      name: t('v2.global.filterChip_label_allLanguages'),
    };

    const options: { id: string; value: number; name: string }[] = [];
    const languageLabel = new Intl.DisplayNames([i18n.language], {
      type: 'language',
    });

    languages.forEach((language: string, index: number) => {
      options.push({
        id: language,
        value: index + 1,
        name: `${language && languageLabel.of(language)}`,
      });
    });

    return [
      allLanguages,
      ...options.sort((a, b) => {
        return a.name.toLowerCase().localeCompare(b.name.toLowerCase());
      }),
    ];
  };

  const filterByLanguage = (events: Array<IBookingEvent>) => {
    if (languageFilter === 0) {
      return events;
    }

    const filteredOptions = getFilterOptions(languages);
    const languageName = filteredOptions.find(
      (option: any) => option.value === languageFilter,
    )!.id;

    return events.filter((event: any) => event.language === languageName);
  };

  const topicsFromEvents = uniq(
    flatten(upcomingEvents.map((item: any) => item.topics)),
  );

  const availableTopics = filter(topics, (item) =>
    topicsFromEvents.includes(item.key),
  );

  const { topic, handleChangeTopic, loading, filterByTopic } = useTopicFilter({
    availableTopics,
  });

  const combineFilters = (events: Array<IBookingEvent>) => {
    return filterByTopic(filterByEventType(filterByLanguage(events)));
  };

  useEffect(() => {
    setFilteredUpcomingEvents(upcomingEvents);
    setLanguages(new Set(upcomingEvents.map((event: any) => event.language)));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [upcomingEvents]);

  useEffect(() => {
    setFilteredUpcomingEvents(combineFilters(upcomingEvents));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventTypeFilter, languageFilter, upcomingEvents, topic]);

  const handleTypeFilterChange = (event: ChangeEvent<HTMLInputElement>) => {
    setEventTypeFilter(event.target.value);
    const format = event.target.value === '' ? undefined : event.target.value;

    dispatch(setFilters({ ...filters, format }));
  };

  const handleLanguageFilterChange = (event: ChangeEvent<HTMLInputElement>) => {
    setLanguageFilter(event.target.value);
    const languageCode = event.target.value
      ? event.target.value.slice(0, -1)
      : undefined;

    dispatch(setFilters({ ...filters, language: languageCode }));
  };

  const getTypeFilterOptions = () => {
    const availableEventTypes = upcomingEvents.flatMap(
      (event: IBookingEvent) => event.type,
    );

    const options = Object.entries(EventType)
      .filter(([, value]) => availableEventTypes.includes(value))
      .map(([, value]) => ({
        value,
        name: t(
          `v2.global.label_${
            value === EventType.EXCHANGE_GROUP
              ? 'exchangeGroup'
              : value.toLowerCase()
          }`,
        ),
      }));

    const sortedOptions = orderBy(options, 'name');

    return [
      { value: '', name: t('v2.global.filterChip_label_eventType') },
      ...sortedOptions,
    ];
  };

  const handleResetFilters = () => {
    setLanguageFilter(0);
    setEventTypeFilter('');
    setFilteredUpcomingEvents(filterByTopic(upcomingEvents));
    dispatch(resetFilters());
  };

  return {
    handleChangeTopic,
    handleTypeFilterChange,
    handleLanguageFilterChange,
    handleResetFilters,
    loading,
    availableTopics,
    topic,
    eventTypeFilter,
    languageFilter,
    getTypeFilterOptions,
    getFilterOptions,
    filteredUpcomingEvents,
    languages,
  };
};

export default useOfferingFilters;
